import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { projects } from "../utils/data";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/* Card number and React key are derived from array position, so adding a
   project at the top of projects.list renumbers everything below it with
   no manual edits. `category` is normalized to an array here so a project
   can sit in more than one filter bucket. */
const catalogue = projects.list.map((p, i) => ({
  ...p,
  key: slug(p.title),
  index: String(i + 1).padStart(2, "0"),
  categories: [].concat(p.category),
}));

/* Filter chips come from the data itself — a new category name on any
   project creates its own chip, in first-appearance order. */
const filters = ["All", ...new Set(catalogue.flatMap((p) => p.categories))];

/* Asymmetric 12-col rhythm: wide, narrow, narrow, wide … so rows alternate
   7+5 and 5+7 instead of marching in identical columns. */
const spanFor = (i, total) => {
  if (total === 1) return "lg:col-span-12";
  return [0, 3].includes(i % 4) ? "lg:col-span-7" : "lg:col-span-5";
};

const ProjectCard = ({ project, span, i }) => {
  const [open, setOpen] = useState(false);
  const hasDemo = Boolean(project.demoLink);

  const Media = hasDemo ? "a" : "div";
  const mediaProps = hasDemo
    ? {
        href: project.demoLink,
        target: "_blank",
        rel: "noopener noreferrer",
        "aria-label": `Open ${project.title} in a new tab`,
      }
    : {};

  return (
    <motion.article
      layout
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={`group col-span-12 ${span} flex flex-col border border-paper-300 bg-white transition-colors duration-200 hover:border-ink-700`}
    >
      {/* Media — flat, sharp, no overlay */}
      <Media
        {...mediaProps}
        className="relative block aspect-[16/10] overflow-hidden border-b border-paper-300 bg-paper-200"
      >
        <img
          src={`assets/images/${project.imgSrc}`}
          alt={`${project.title}: ${project.subtitle}`}
          loading={i < 2 ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-fill object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />

        <span className="absolute left-4 top-4 font-mono text-micro text-ink-500">
          {project.index}
        </span>
        <span className="absolute right-4 top-4 border border-paper-400 bg-paper-100 px-2.5 py-1 font-mono text-micro uppercase text-ink-600">
          {project.categories.join(" · ")}
        </span>
      </Media>

      {/* Body */}
      <div className="flex flex-1 flex-col p-6 lg:p-7">
        <h3 className="display text-2xl tracking-tight text-ink-950 lg:text-3xl">
          {project.title}
        </h3>
        <p className="mt-1.5 text-sm text-clay-dim">{project.subtitle}</p>
        <p className="mt-4 text-sm leading-relaxed text-ink-600">
          {project.description}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {project.technologies.map((tech) => (
            <span
              key={tech}
              className="border border-paper-300 bg-paper-100 px-2.5 py-1 font-mono text-micro uppercase text-ink-500"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Expandable detail keeps cards scannable but the substance available */}
        <div className="mt-auto pt-6">
          <button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex items-center gap-2 font-mono text-micro uppercase text-ink-500 transition-colors hover:text-ink-950"
          >
            {open ? <Minus size={13} /> : <Plus size={13} />}
            {open ? "Hide detail" : "In detail"}
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
              >
                <ul className="mt-4 space-y-2.5 border-t border-paper-300 pt-4">
                  {project.features.map((f) => (
                    <li
                      key={f}
                      className="flex gap-3 text-sm leading-relaxed text-ink-600"
                    >
                      <span
                        aria-hidden
                        className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-clay"
                      />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          {!hasDemo && (
            <p className="mt-4 font-mono text-micro uppercase text-ink-500">
              Private / no live demo
            </p>
          )}
        </div>
      </div>
    </motion.article>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(
    () =>
      filter === "All"
        ? catalogue
        : catalogue.filter((p) => p.categories.includes(filter)),
    [filter],
  );

  return (
    <section
      id="work"
      className="scroll-mt-20 border-b border-paper-300 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-shell px-6 lg:px-10">
        {/* Split header */}
        <div className="grid gap-8 border-b border-paper-300 pb-10 lg:grid-cols-12 lg:items-end lg:gap-10">
          <div className="lg:col-span-7">
            <p className="eyebrow eyebrow-dot">{projects.eyebrow}</p>
            <h2 className="display mt-5 text-display-md text-ink-950">
              {projects.heading}
            </h2>
          </div>
          <p className="text-balance text-base leading-relaxed text-ink-600 lg:col-span-5">
            {projects.desc}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2.5 py-8">
          {filters.map((f) => {
            const active = f === filter;
            const count =
              f === "All"
                ? catalogue.length
                : catalogue.filter((p) => p.categories.includes(f)).length;

            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                aria-pressed={active}
                className={`flex items-center gap-2 border px-4 py-2.5 font-display text-sm font-semibold tracking-tight transition-colors ${
                  active
                    ? "border-ink-950 bg-ink-950 text-paper"
                    : "border-paper-400 text-ink-600 hover:border-ink-950 hover:text-ink-950"
                }`}
              >
                {f}
                <span
                  className={`font-mono text-micro ${
                    active ? "text-paper/60" : "text-ink-400"
                  }`}
                >
                  {String(count).padStart(2, "0")}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <motion.div layout className="grid grid-cols-12 gap-5 lg:gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((project, i) => (
              <ProjectCard
                key={project.key}
                project={project}
                span={spanFor(i, filtered.length)}
                i={i}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
